import { signInWithEmailAndPassword } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import moment from 'moment';
import 'moment/locale/uk';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import AppFallback from './components/AppFallback';
import AuthForm from './components/AuthForm';
import Mobile from './components/Mobile';
import Web from './components/Web';
import { AuthContext } from './context/AuthContext';
import './firebase/firebase';
import { useFirebaseMessaging } from './hooks/useFirebaseMessaging';

import { useIsMobile } from './hooks/useIsMobile';
import { useOpenChatEvents } from './hooks/useOpenChatEvents';
import { useChatListSubscription } from './hooks/useChatListSubscription';
import { useChatMessagesSubscription } from './hooks/useChatMessagesSubscription';
import { createHandleMessageReceived } from './services/notifications';
import { markLastMessageAsRead } from './services/chatService';

function App() {
  const [chats, setChats] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showAppFallback, setShowAppFallback] = useState(false);
  moment.locale('uk');

  const [selectedChatId, setSelectedChatId] = useState(null);
  const [highlightedChatId, setHighlightedChatId] = useState(null);

  const highlight = useCallback((id) => {
    if (!id) return;
    setHighlightedChatId(id);
    setTimeout(() => {
      setHighlightedChatId((cur) => (cur === id ? null : cur));
    }, 3000);
  }, []);

  const isMobile = useIsMobile();
  const db = getFirestore();
  const { user, auth } = useContext(AuthContext);

  // Кеш для повідомлень
  const messagesCache = useRef({});

  // Обробка вхідних push-повідомлень
  const handleMessageReceived = useMemo(
    () => createHandleMessageReceived({ db, highlight, setChats }),
    [db, highlight, setChats]
  );

  // Використовуємо хук для обробки Firebase messaging
  useFirebaseMessaging({ onMessageReceived: handleMessageReceived });

  // Відкриття конкретного чату по кліку на нотифікацію (із SW або foreground)
  useOpenChatEvents({
    onOpen: (chatId) => {
      setSelectedChatId(chatId);
      highlight(chatId);
    },
  });

  // Зчитати chatId з URL (?chatId=...) і очистити його після відкриття
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('chatId');
    const noRedirect = params.get('no_redirect');

    if (id) {
      setSelectedChatId(id);
      highlight(id);
      params.delete('chatId');
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : '') +
        window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }

    // Показати fallback тільки якщо не авторизований і не встановлений флаг no_redirect
    if (!user && !noRedirect) {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /iphone|ipad|ipod|android/.test(userAgent);

      // Показуємо fallback для мобільних пристроїв або якщо це deep link
      if (isMobileDevice || id) {
        setShowAppFallback(true);
      }
    }
  }, [highlight, user]);

  // Підписка тільки на чати
  useChatListSubscription({
    user,
    db,
    messagesCacheRef: messagesCache,
    setChats,
  });

  // Підписка на повідомлення вибраного чату
  useChatMessagesSubscription({
    user,
    db,
    selectedChatId,
    messagesCacheRef: messagesCache,
    setChats,
  });

  // Позначити останнє повідомлення як прочитане при відкритті чату
  useEffect(() => {
    if (selectedChatId) {
      markLastMessageAsRead(db, selectedChatId);
    }
  }, [selectedChatId, db]);

  // Очищення локального стану при виході користувача
  useEffect(() => {
    if (!user) {
      messagesCache.current = {};
      setChats([]);
    }
  }, [user]);

  const chat = useMemo(
    () => chats.find((c) => c.id === selectedChatId),
    [chats, selectedChatId]
  );

  const handleSignIn = async (e) => {
    e.preventDefault();

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setError(null);
    } catch (err) {
      setError('Не вдалося увійти. Перевірте email та пароль.');
    }
  };

  if (!user) {
    return (
      <div className='auth-modal'>
        <AuthForm
          email={email}
          password={password}
          setEmail={setEmail}
          setPassword={setPassword}
          handleSignIn={handleSignIn}
          error={error}
        />
      </div>
    );
  }

  // Показати AppFallback якщо потрібно
  if (showAppFallback) {
    return (
      <AppFallback
        onAppDetected={() => {
          setShowAppFallback(false);
        }}
      />
    );
  }

  if (isMobile) {
    return (
      <Mobile
        chat={chat}
        chats={chats}
        selectedChatId={selectedChatId}
        setSelectedChatId={setSelectedChatId}
        highlightedChatId={highlightedChatId}
      />
    );
  } else {
    return (
      <Web
        chat={chat}
        chats={chats}
        selectedChatId={selectedChatId}
        setSelectedChatId={setSelectedChatId}
        highlightedChatId={highlightedChatId}
      />
    );
  }
}

export default App;
